import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Crown, Users, Video } from 'lucide-react';
import { CPM_CLIPPER_LEADERBOARD, MY_CPM_LEADERBOARD_STANDING, MY_CPM_LEADERBOARD_VIEWER } from '@/queries/queries';
import { MY_CLIPPER_RANK_PREVIEW } from '@/queries/ranks';
import { formatLeaderboardViews } from '@/lib/cpm/formatLeaderboardViews';
import { leaderboardRowDisplay } from '@/lib/cpm/leaderboardRowDisplay';
import { buildStandingLeaderboardItems } from '@/lib/cpm/buildStandingLeaderboardItems';
import { resolvePreviewChrome } from '@/lib/ranks/resolvePreviewChrome';
import { resolveNestLayout } from '@/lib/ranks/resolveNestLayout';
import { resolveRowFamilies } from '@/lib/ranks/resolveRowFamilies';
import type { ClipperRankFamily } from '@/lib/ranks/types';
import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { RankFamilyNests } from '@/components/ranks/RankFamilyNests';
import { LeaderboardAnonymousToggle } from '@/components/cpm/LeaderboardAnonymousToggle';
import { ClipperLeaderboardAvatar } from '@/components/cpm/ClipperLeaderboardAvatar';
import { cn } from '@/lib/utils';

export type CpmClipperLeaderboardEntry = {
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
  total_views: string;
  video_count: number;
  anonymous?: boolean | null;
  views_tier_id?: string | null;
  rank?: number | null;
  is_viewer?: boolean | null;
  unranked?: boolean | null;
  families?: ClipperRankFamily[] | null;
};

type QueryResult = {
  cpmClipperLeaderboard?: CpmClipperLeaderboardEntry[];
  myCpmLeaderboardStanding?: {
    rank: number | null;
    total: number;
    entries: CpmClipperLeaderboardEntry[];
  };
  myData?: {
    id?: string | null;
    name?: string | null;
    profile_picture?: string | null;
    onboarding_objective?: string | null;
  } | null;
  myClipperRankPreview?: {
    views_tier_id?: string | null;
    season_id?: string | null;
    season_name?: string | null;
    season_title_id?: string | null;
  } | null;
};

function rankLabel(
  entry: CpmClipperLeaderboardEntry,
  t: ReturnType<typeof useTranslation>['t'],
): string | null {
  if (entry.unranked) return t('cpmLeaderboard.unrankedRank');
  if (entry.rank != null) return t('cpmLeaderboard.rank', { rank: entry.rank });
  return null;
}

function LeaderboardFamilyNests({ entry }: { entry: CpmClipperLeaderboardEntry }) {
  const layout = resolveNestLayout({ surface: 'leaderboard' });
  return (
    <RankFamilyNests
      families={resolveRowFamilies({
        families: entry.families,
        views_tier_id: entry.unranked ? null : entry.views_tier_id,
      })}
      leadingFamilyId={layout.leadingFamilyId}
      labeledFamilyId={layout.labeledFamilyId}
    />
  );
}

function Row({ entry, language, t, showRank }: {
  entry: CpmClipperLeaderboardEntry;
  language: string;
  t: ReturnType<typeof useTranslation>['t'];
  showRank?: boolean;
}) {
  const views = formatLeaderboardViews(entry.total_views, language);
  const display = leaderboardRowDisplay({
    ...entry,
    anonymous: entry.anonymous && !entry.is_viewer,
  });

  if (display.isAnonymous) {
    return (
      <div
        className="flex items-center gap-3 py-2.5 px-3"
        aria-label={t('cpmLeaderboard.anonymousAria', { views })}
        data-testid="cpm-leaderboard-anonymous-row"
      >
        {showRank && rankLabel(entry, t) && (
          <span className="w-8 shrink-0 text-xs font-bold text-primary tabular-nums">
            {rankLabel(entry, t)}
          </span>
        )}
        <ClipperLeaderboardAvatar
          anonymous
          anonymousTestId="cpm-leaderboard-anonymous-avatar"
        />
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <p
            data-testid="cpm-leaderboard-anonymous-name"
            aria-hidden="true"
            className="min-w-0 truncate text-sm font-semibold text-primary blur-sm select-none"
          >
            {t('cpmLeaderboard.anonymousName')}
          </p>
          <LeaderboardFamilyNests entry={entry} />
        </div>
        <div className="flex flex-col items-end gap-0.5 flex-shrink-0 text-primary">
          <span className="inline-flex items-center gap-1 text-xs font-semibold">
            <Users className="w-3 h-3" aria-hidden="true" />
            {t('cpmLeaderboard.views', { views })}
          </span>
        </div>
      </div>
    );
  }

  const name = display.name || '';
  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2.5 px-3',
        entry.is_viewer && 'bg-primary/15',
      )}
      data-testid={entry.is_viewer ? 'cpm-leaderboard-viewer-row' : undefined}
    >
      {showRank && rankLabel(entry, t) && (
        <span className="w-8 shrink-0 text-xs font-bold text-primary tabular-nums">
          {rankLabel(entry, t)}
        </span>
      )}
      <ClipperLeaderboardAvatar name={name} avatarUrl={display.avatarUrl} />
      <div className="min-w-0 flex-1 flex items-center gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-primary">{name}</p>
        {entry.is_viewer && (
          <span
            data-testid="cpm-leaderboard-you-badge"
            className="shrink-0 rounded-md border border-primary/50 bg-primary/30 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-primary"
          >
            {t('cpmLeaderboard.you')}
          </span>
        )}
        <LeaderboardFamilyNests entry={entry} />
      </div>
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0 text-primary">
        {entry.unranked ? (
          <span className="text-[11px] font-semibold text-primary/80">
            {t('cpmLeaderboard.unranked')}
          </span>
        ) : (
        <span className="inline-flex items-center gap-1 text-xs font-semibold">
          <Users className="w-3 h-3" aria-hidden="true" />
          {t('cpmLeaderboard.views', { views })}
        </span>
        )}
        {!entry.unranked && display.showVideoCount && (
          <span className="inline-flex items-center gap-1 text-[11px] text-primary/80">
            <Video className="w-3 h-3" aria-hidden="true" />
            {t('cpmLeaderboard.videos', { count: entry.video_count })}
          </span>
        )}
      </div>
    </div>
  );
}

export function CpmClipperLeaderboard({ variant }: { variant: 'sidebar' | 'split' | 'standing' }) {
  const { t, i18n } = useTranslation();
  const isStanding = variant === 'standing';
  const boardQuery = useQuery<QueryResult>(CPM_CLIPPER_LEADERBOARD, { errorPolicy: 'all' });
  const standingQuery = useQuery<QueryResult>(MY_CPM_LEADERBOARD_STANDING, {
    skip: !isStanding,
    errorPolicy: 'all',
  });
  const viewerQuery = useQuery<QueryResult>(MY_CPM_LEADERBOARD_VIEWER, {
    errorPolicy: 'all',
  });
  const previewQuery = useQuery<QueryResult>(MY_CLIPPER_RANK_PREVIEW, {
    errorPolicy: 'all',
  });
  const loading = isStanding
    ? (boardQuery.loading && !boardQuery.data)
      || (standingQuery.loading && !standingQuery.data)
      || (viewerQuery.loading && !viewerQuery.data)
    : boardQuery.loading && !boardQuery.data;
  const skeletonCount = isStanding ? 10 : 5;
  const refetch = () => {
    void boardQuery.refetch();
    if (isStanding) {
      void standingQuery.refetch();
      void viewerQuery.refetch();
    }
  };

  if (loading) {
    return (
      <div
        data-testid="cpm-clipper-leaderboard-skeleton"
        className="rounded-xl border border-primary/20 overflow-hidden bg-card"
      >
        <div className="h-11 bg-primary/15 animate-pulse" />
        <div className="divide-y divide-primary/10">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5 px-3">
              <div className="w-9 h-9 rounded-full bg-muted animate-pulse" />
              <div className="h-3 flex-1 bg-muted animate-pulse rounded w-full" />
              <div className="h-3 w-16 bg-muted animate-pulse rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const standing = standingQuery.data?.myCpmLeaderboardStanding ?? null;
  const boardEntries = boardQuery.data?.cpmClipperLeaderboard ?? [];
  const standingItems = isStanding
    ? buildStandingLeaderboardItems({
      topEntries: boardEntries,
      standing,
      viewer: viewerQuery.data?.myData
        ? {
          user_id: viewerQuery.data.myData.id,
          name: viewerQuery.data.myData.name,
          avatar_url: viewerQuery.data.myData.profile_picture,
        }
        : null,
      viewerViewsTierId: previewQuery.data?.myClipperRankPreview?.views_tier_id,
    })
    : [];
  const entries = isStanding ? standingItems.filter((item) => item.type === 'row').map((item) => item.entry) : boardEntries;
  if (boardQuery.error || entries.length === 0) return null;
  const preview = previewQuery.data?.myClipperRankPreview;
  const { showChrome, showTier, showSeason, views_tier_id, season_name, season_title_id } = resolvePreviewChrome({
    isOtherChannels: viewerQuery.data?.myData?.onboarding_objective === 'other_channels',
    previewError: !!previewQuery.error,
    views_tier_id: preview?.views_tier_id ?? null,
    season_id: preview?.season_id ?? null,
    season_name: preview?.season_name ?? null,
    season_title_id: preview?.season_title_id ?? null,
  });

  return (
    <section
      data-testid="cpm-clipper-leaderboard"
      className="rounded-xl border border-primary/25 overflow-hidden bg-card"
    >
      <header className="flex flex-col items-center justify-center gap-0.5 bg-primary/15 text-primary py-2.5 px-3">
        <div className="flex items-center justify-center gap-2">
          <Crown className="w-4 h-4" aria-hidden="true" />
          <h2 className="text-sm font-bold">{t('cpmLeaderboard.title')}</h2>
        </div>
        {isStanding && (
          <p className="text-[11px] font-medium text-primary/80" data-testid="cpm-leaderboard-standing-label">
            {standing?.rank != null
              ? t('cpmLeaderboard.yourPosition', { rank: standing.rank, total: standing.total })
              : t('cpmLeaderboard.unranked')}
          </p>
        )}
      </header>
      {showChrome && (
        <div
          data-testid="cpm-leaderboard-ranks-preview"
          className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 px-3 py-1.5 text-[11px] font-medium text-primary/80 border-b border-primary/10"
        >
          {showTier && views_tier_id && (
            <RankPatentBadge tierId={views_tier_id} size="sm" className="text-[11px]" />
          )}
          {showSeason && (
            <>
              {season_title_id && (
                <RankPatentBadge tierId={season_title_id} size="sm" className="text-[11px]" />
              )}
              {season_name && <span>{season_name}</span>}
            </>
          )}
          <Link to="/rankings" className="text-primary underline-offset-2 hover:underline">
            {t('cpmLeaderboard.ranksLink')}
          </Link>
        </div>
      )}
      <div
        className={cn(
          'divide-y divide-primary/10',
          variant === 'split' && 'sm:divide-y-0 sm:grid sm:grid-cols-2 sm:divide-x sm:divide-primary/10',
        )}
      >
        {variant === 'split' ? (
          <>
            <div className="divide-y divide-primary/10">
              {entries.slice(0, 5).map((entry, index) => (
                <Row key={entry.anonymous ? `anon-${index}` : entry.user_id} entry={entry} language={i18n.language} t={t} />
              ))}
            </div>
            <div className="divide-y divide-primary/10">
              {entries.slice(5).map((entry, index) => (
                <Row key={entry.anonymous ? `anon-${index + 5}` : entry.user_id} entry={entry} language={i18n.language} t={t} />
              ))}
            </div>
          </>
        ) : isStanding ? (
          standingItems.map((item, index) => (
            item.type === 'gap' ? (
              <div
                key="gap"
                data-testid="cpm-leaderboard-gap"
                className="flex items-center justify-center py-2 text-sm font-bold tracking-[0.35em] text-primary/50"
                aria-hidden="true"
              >
                {t('cpmLeaderboard.gap')}
              </div>
            ) : (
              <Row
                key={item.entry.is_viewer ? 'viewer' : item.entry.anonymous ? `anon-${item.entry.rank ?? index}` : item.entry.user_id}
                entry={item.entry}
                language={i18n.language}
                t={t}
                showRank
              />
            )
          ))
        ) : (
          entries.map((entry, index) => (
            <Row
              key={entry.anonymous ? `anon-${index}` : entry.user_id}
              entry={entry}
              language={i18n.language}
              t={t}
            />
          ))
        )}
      </div>
      <LeaderboardAnonymousToggle variant="compact" onChanged={() => { void refetch(); }} />
    </section>
  );
}
